import Product from '@models/product';

/**
 * Converts a product title to a URL-friendly slug
 * @param title - The product title to convert
 * @returns URL-friendly slug
 */
export const createSlugFromTitle = (title: string): string => {
  return title
    .toLowerCase()
    .trim()
    // Replace special characters and spaces with hyphens
    .replace(/[^\w\s-]/g, '')
    // Replace multiple spaces/hyphens with single hyphen
    .replace(/[\s_-]+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '')
    // Limit length to avoid very long URLs
    .substring(0, 100);
};

/**
 * Generates SEO-friendly URL for the product details page.
 * Format: /view/{product-name}-{id}
 * @param product - Product object containing id and title
 * @returns SEO-friendly URL string
 */
export const generateProductDetailsUrl = (product: Product | { id: string; title: string }): string => {
  const slug = createSlugFromTitle(product.title);
  return `/view/${slug}-${product.id}`;
};

/** @deprecated Use {@link generateProductDetailsUrl} — both now return `/view/...`. */
export const generateProductViewUrl = (product: Product | { id: string; title: string }): string => {
  return generateProductDetailsUrl(product);
};

/**
 * Extracts product ID from SEO-friendly URL
 * @param urlPath - The URL path (e.g., "product-name-a69884f1-078a-42ae-99fb-6a302f7ea617" or full URL)
 * @returns Product ID string or null if not found
 */
export const extractProductIdFromUrl = (urlPath: string): string | null => {
  // Handle full paths (/view/..., /product/...) or the route param segment only
  let pathSegment = urlPath;
  if (urlPath.includes('/view/')) {
    pathSegment = urlPath.split('/view/')[1] ?? urlPath;
  } else if (urlPath.includes('/product/')) {
    pathSegment = urlPath.split('/product/')[1] ?? urlPath;
  }
  
  if (!pathSegment) return null;
  
  // UUID pattern: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;
  const uuidMatch = pathSegment.match(uuidPattern);
  
  if (uuidMatch) {
    return uuidMatch[0];
  }
  
  // Fallback: if no UUID found, try extracting from the end (for non-UUID IDs)
  const lastHyphenIndex = pathSegment.lastIndexOf('-');
  if (lastHyphenIndex === -1) return null;
  
  const potentialId = pathSegment.substring(lastHyphenIndex + 1);
  
  // Validate that it looks like an ID
  if (potentialId.length > 0 && /^[a-zA-Z0-9_-]+$/.test(potentialId)) {
    return potentialId;
  }
  
  return null;
};

/**
 * Validates if a URL path matches the expected SEO-friendly format
 * @param urlPath - The URL path to validate
 * @returns boolean indicating if format is valid
 */
export const isValidProductUrl = (urlPath: string): boolean => {
  const productId = extractProductIdFromUrl(urlPath);
  return productId !== null;
};

/**
 * Optimizes image URL using imagic.edge-net.co service
 * @param imageUrl - The original image URL
 * @param width - Image width (default: 240)
 * @param height - Image height (default: 320)
 * @param dpr - Device pixel ratio (default: 1)
 * @returns Optimized image URL or original URL if optimization fails
 */
export const optimizeImageUrl = (
  imageUrl: string | null | undefined,
  width: number = 240,
  height: number = 320,
  dpr: number = 1
): string => {
  // Return default or empty if no URL provided
  if (!imageUrl || imageUrl.trim() === '') {
    return '';
  }

  // If already optimized or is a data URL, return as is
  if (imageUrl.includes('imagic.edge-net.co') || imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')) {
    return imageUrl;
  }

  // If it's a local/public URL, return as is
  if (imageUrl.startsWith('/') || (process.env.PUBLIC_URL && imageUrl.startsWith(process.env.PUBLIC_URL))) {
    return imageUrl;
  }

  try {
    // Encode the original URL to base64
    // Use encodeURIComponent first to handle special characters, then btoa
    const encodedUrl = btoa(unescape(encodeURIComponent(imageUrl)));
    // Construct the optimized URL
    return `https://imagic.edge-net.co/magic/rs:fill:${width}:${height}:${dpr}/dpr:${dpr}/${encodedUrl}.webp`;
  } catch (error) {
    // If encoding fails, return original URL
    console.warn('Failed to optimize image URL:', error);
    return imageUrl;
  }
}; 